import { Reducer } from 'react';
import { GetViewIdentifier } from '../components/reporting/GroupAccessOwnersForHierarchyConfig';
import { AudienceSourceType, DistributionType, IProfile, IValidationCollection } from '../models';
import { TFetchableEntity } from '../models/IFetchableEntity';
import IHierarchyGroupAccess from '../models/IHierarchyGroupAccess';
import { IHierarchyReportingAccessConfig } from '../models/IHierarchyReportingAccessConfig';
import { getHierarchyGroupAccesses, getProfiles } from '../services/HierarchyAPI';
import * as ReportingUsersAPI from '../services/ReportingUsersAPI';
import { getAudienceSource } from '../services/SurveyAPI';
import { DistributionHelper } from '../utilities/DistributionHelper';
import { CanUserAccessFeature } from '../utilities/FeatureFlags';
import { TThunkAction } from './';
import * as Actions from './Actions';
import { ActionTypes } from './ActionTypes';
import { GroupAccessOwnerViewActions, GroupAccessOwnerViewActionTypes, IsAGroupAccessOwnerViewAction } from './GroupAccessOwners/ViewStore';

export interface IHierarchyReportingAccessState {
	editConfig: IHierarchyReportingAccessConfig | undefined;
	enableAddBulkRow: boolean;
	hierarchies: TFetchableEntity<IHierarchyGroupAccess[]>;
	hierarchyReportingAccessConfigs: IHierarchyReportingAccessConfig[];
	hrisId?: string;
	isValid: boolean;
	profiles: TFetchableEntity<IProfile[]>;
	fetchingGroupAccessOwners: boolean | 'N/A';
	saving: boolean;
	showAddBulkRow: boolean;
	showDeleteConfigModal: boolean;
	showEditConfigModal: boolean;
	validationResults?: IValidationCollection;
	outOfSyncHierarchyReportingAccessConfigId: number[];
}

export const defaultState: IHierarchyReportingAccessState = {
	editConfig: undefined,
	enableAddBulkRow: false,
	hierarchies: { isLoading: true },
	hierarchyReportingAccessConfigs: [],
	isValid: true,
	profiles: { isLoading: true },
	fetchingGroupAccessOwners: false,
	saving: false,
	showAddBulkRow: false,
	showDeleteConfigModal: false,
	showEditConfigModal: false,
	outOfSyncHierarchyReportingAccessConfigId: [],
};

type TActionCreator<R> = TThunkAction<R, KnownActions | Actions.TNotificationActions>;

export const ActionCreators = {
	AddHierarchyReportingAccessConfig:
		(surveyId: number, model: IHierarchyReportingAccessConfig): TActionCreator<void> =>
		async (dispatch) => {
			dispatch({ type: ActionTypes.TOAST_PROCESSING_NOTIFICATION, payload: '' });
			dispatch({ type: ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_ADD_REQUESTED });
			const resultsModel = await ReportingUsersAPI.addHierarchyReportingAccessConfig(surveyId, model);
			if (resultsModel.isValid) {
				dispatch({ type: ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_ADD_SUCCEEDED, payload: resultsModel });
				dispatch({ type: ActionTypes.TOAST_SUCCESS_GENERIC_NOTIFICATION });
			} else {
				dispatch({ type: ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_ADD_FAILED, payload: resultsModel.validationResults });
				dispatch({ type: ActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
			}
		},
	CloseDeleteConfigModal: () => ({ type: ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_DELETE_MODAL_CLOSE }),
	CloseEditConfigModal: () => ({ type: ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_EDIT_MODAL_CLOSE }),
	DeleteHierarchyReportingAccessConfig:
		(surveyId: number, config: IHierarchyReportingAccessConfig): TActionCreator<void> =>
		async (dispatch) => {
			dispatch({ type: ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_DELETE_REQUESTED });
			await ReportingUsersAPI.deleteHierarchyReportingAccessConfig(surveyId, config);
			dispatch({ type: ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_DELETE_SUCCEEDED, payload: config });
		},
	GetHierarchyReportingAccessConfigs:
		(surveyId: number): TActionCreator<void> =>
		async (dispatch) => {
			dispatch({ type: ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_GET_REQUESTED });
			const configs = await ReportingUsersAPI.getHierarchyReportingAccessConfigs(surveyId);
			dispatch({ type: ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_GET_SUCCEEDED, payload: configs });
		},
	InitializeState:
		(surveyId: number, distributionType: DistributionType): TActionCreator<void> =>
		async (dispatch, getState) => {
			const { externalHrisId: hrisId, audienceSourceType: sourceType } = await getAudienceSource(surveyId);
			const { user: forUser } = getState().oidc;

			const showAddBulkRow = CanUserAccessFeature(forUser, 'EnableReportingProfiles') && sourceType === AudienceSourceType.Hris;
			const enableAddBulkRow = DistributionHelper.isNotAnonymous(distributionType);

			if (showAddBulkRow && enableAddBulkRow && hrisId) {
				// ======== Fetch hierarchy group access info ======== //
				dispatch({ type: ActionTypes.REPORTING_ACCESS_HIERARCHIES_BEGIN_FETCHING });

				try {
					const hierarchies = await getHierarchyGroupAccesses(hrisId);

					dispatch({
						type: ActionTypes.REPORTING_ACCESS_HIERARCHIES_FETCHED,
						payload: hierarchies.filter((h) => h.groupAccessType),
					});
				} catch {
					dispatch({ type: ActionTypes.REPORTING_ACCESS_HIERARCHIES_FAILED_TO_FETCH });
				}

				// ======== Fetch profile info ======== //
				dispatch({ type: ActionTypes.REPORTING_ACCESS_PROFILES_BEGIN_FETCHING });

				try {
					const profiles = await getProfiles(hrisId);

					dispatch({ type: ActionTypes.REPORTING_ACCESS_PROFILES_FETCHED, payload: profiles });
				} catch {
					dispatch({ type: ActionTypes.REPORTING_ACCESS_PROFILES_FAILED_TO_FETCH });
				}

				// ======== Fetch reporting access config ======== //
				dispatch(ActionCreators.GetHierarchyReportingAccessConfigs(surveyId));
			}

			dispatch({
				type: ActionTypes.REPORTING_ACCESS_HIERARCHY_SECTION_INITIALIZE_SUCCEEDED,
				payload: {
					enableAddBulkRow,
					hrisId,
					showAddBulkRow,
				},
			});
		},
	OpenDeleteConfigModal: (config?: IHierarchyReportingAccessConfig) => ({
		type: ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_DELETE_MODAL_OPEN,
		payload: config,
	}),
	OpenEditConfigModal: (config?: IHierarchyReportingAccessConfig) => ({
		type: ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_EDIT_MODAL_OPEN,
		payload: config,
	}),

	UpdateHierarchyReportingAccessConfig:
		(surveyId: number, config: IHierarchyReportingAccessConfig, closeModal: boolean): TActionCreator<void> =>
		async (dispatch) => {
			dispatch({ type: ActionTypes.TOAST_PROCESSING_NOTIFICATION, payload: '' });
			dispatch({ type: ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_UPDATE_REQUESTED });
			const resultsModel = await ReportingUsersAPI.updateHierarchyReportingAccessConfig(surveyId, config);
			if (resultsModel.isValid) {
				dispatch({
					type: ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_UPDATE_SUCCEEDED,
					payload: {
						config: resultsModel.subject,
						closeModal,
					},
				});
				dispatch({ type: ActionTypes.TOAST_SUCCESS_GENERIC_NOTIFICATION });
			} else {
				dispatch({
					type: ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_UPDATE_FAILED,
					payload: resultsModel.validationResults,
				});
				dispatch({ type: ActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
			}
		},
	UpdateOutOfSyncHierarchyReportingAccessConfigUserCount:
		(surveyId: number, hierarchyReportingAccessConfigId: number, userCount: number): TActionCreator<void> =>
		async (dispatch) => {
			await ReportingUsersAPI.patchHierarchyReportingAccessConfigUserCount(surveyId, hierarchyReportingAccessConfigId, userCount);
			dispatch({
				type: ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_UPDATE_USER_COUNT_SUCCEEDED,
				payload: { hierarchyReportingAccessConfigId },
			});
		},
};

export type KnownActions =
	| Actions.IHierarchyReportingAccessConfigsAddFailed
	| Actions.IHierarchyReportingAccessConfigDeleteModalClose
	| Actions.IHierarchyReportingAccessConfigDeleteModalOpen
	| Actions.IHierarchyReportingAccessConfigEditModalClose
	| Actions.IHierarchyReportingAccessConfigEditModalOpen
	| Actions.IHierarchyReportingAccessConfigsAddRequested
	| Actions.IHierarchyReportingAccessConfigsAddSucceeded
	| Actions.IHierarchyReportingAccessConfigsDeleteRequested
	| Actions.IHierarchyReportingAccessConfigsDeleteSucceeded
	| Actions.IHierarchyReportingAccessConfigsGetRequested
	| Actions.IHierarchyReportingAccessConfigsGetSucceeded
	| Actions.IHierarchyReportingAccessConfigsUpdateFailed
	| Actions.IHierarchyReportingAccessConfigsUpdateRequested
	| Actions.IHierarchyReportingAccessConfigsUpdateSucceeded
	| Actions.IHierarchyReportingAccessSectionInitializeSucceeded
	| Actions.IHierarchyReportingAccessSectionHierarchyGroupAccessInfoRequested
	| Actions.IHierarchyReportingAccessSectionHierarchyGroupAccessInfoFetched
	| Actions.IHierarchyReportingAccessSectionHierarchyGroupAccessInfoFailedToFetch
	| Actions.IHierarchyReportingAccessSectionProfilesRequested
	| Actions.IHierarchyReportingAccessSectionProfilesFetched
	| Actions.IHierarchyReportingAccessSectionProfilesFailedToFetch
	| Actions.IHierarchyReportingAccessConfigsUpdateUserCountSucceeded
	| GroupAccessOwnerViewActions;

export const reducer: Reducer<IHierarchyReportingAccessState, KnownActions> = (
	state: IHierarchyReportingAccessState | undefined,
	action: KnownActions,
): IHierarchyReportingAccessState => {
	if (!state) {
		return defaultState;
	}

	switch (action.type) {
		case ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_ADD_REQUESTED:
			return {
				...state,
				saving: true,
			};
		case ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_ADD_SUCCEEDED:
			return {
				...state,
				saving: false,
				showEditConfigModal: false,
				hierarchyReportingAccessConfigs: [...state.hierarchyReportingAccessConfigs, action.payload.subject],
				validationResults: action.payload.validationResults,
			};
		case ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_ADD_FAILED:
			return {
				...state,
				saving: false,
				validationResults: action.payload,
			};
		case ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_DELETE_REQUESTED:
			return {
				...state,
				saving: true,
			};
		case ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_DELETE_SUCCEEDED: {
			const configs = [...state.hierarchyReportingAccessConfigs].filter(
				(c: IHierarchyReportingAccessConfig) => c.hierarchyReportingAccessConfigId !== action.payload.hierarchyReportingAccessConfigId,
			);

			return {
				...state,
				hierarchyReportingAccessConfigs: configs,
				saving: false,
				showDeleteConfigModal: false,
			};
		}
		case ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_DELETE_MODAL_CLOSE:
			return {
				...state,
				showDeleteConfigModal: false,
			};
		case ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_DELETE_MODAL_OPEN:
			return {
				...state,
				editConfig: action.payload,
				showDeleteConfigModal: true,
			};
		case ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_EDIT_MODAL_CLOSE:
			return {
				...state,
				editConfig: undefined,
				showEditConfigModal: false,
				validationResults: undefined,
			};
		case ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_EDIT_MODAL_OPEN:
			return {
				...state,
				editConfig: action.payload,
				showEditConfigModal: true,
			};
		case ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_GET_SUCCEEDED:
			return {
				...state,
				hierarchyReportingAccessConfigs: action.payload,
			};
		case ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_UPDATE_REQUESTED:
			return {
				...state,
				saving: true,
			};
		case ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_UPDATE_SUCCEEDED:
			return {
				...state,
				editConfig: action.payload.closeModal ? undefined : action.payload.config,
				hierarchyReportingAccessConfigs: state.hierarchyReportingAccessConfigs.map((c) => {
					if (c.hierarchyReportingAccessConfigId === action.payload.config.hierarchyReportingAccessConfigId) {
						return action.payload.config;
					}
					return c;
				}),
				saving: false,
				showEditConfigModal: !action.payload.closeModal,
			};
		case ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_UPDATE_USER_COUNT_SUCCEEDED:
			return {
				...state,
				// remove the id of the updated config from the list.
				outOfSyncHierarchyReportingAccessConfigId: state.outOfSyncHierarchyReportingAccessConfigId.filter(
					(id) => id !== action.payload.hierarchyReportingAccessConfigId,
				),
			};
		case ActionTypes.REPORTING_ACCESS_HIERARCHY_CONFIGS_UPDATE_FAILED: {
			return {
				...state,
				saving: false,
				validationResults: action.payload,
			};
		}
		case ActionTypes.REPORTING_ACCESS_HIERARCHY_SECTION_INITIALIZE_SUCCEEDED:
			return {
				...state,
				...action.payload,
			};
		case ActionTypes.REPORTING_ACCESS_HIERARCHIES_BEGIN_FETCHING:
			return {
				...state,
				hierarchies: { isLoading: true },
			};
		case ActionTypes.REPORTING_ACCESS_HIERARCHIES_FETCHED:
			return {
				...state,
				hierarchies: action.payload,
			};
		case ActionTypes.REPORTING_ACCESS_HIERARCHIES_FAILED_TO_FETCH:
			return {
				...state,
				hierarchies: {
					isLoading: false,
					couldNotFetchData: true,
				},
			};
		case ActionTypes.REPORTING_ACCESS_PROFILES_BEGIN_FETCHING:
			return {
				...state,
				profiles: { isLoading: true },
			};
		case ActionTypes.REPORTING_ACCESS_PROFILES_FETCHED:
			return {
				...state,
				profiles: action.payload,
			};
		case ActionTypes.REPORTING_ACCESS_PROFILES_FAILED_TO_FETCH:
			return {
				...state,
				profiles: {
					isLoading: false,
					couldNotFetchData: true,
				},
			};
		default: {
			if (IsAGroupAccessOwnerViewAction(action)) {
				return HandleGroupAccessOwnerActions(state, action);
			}

			return state;
		}
	}
};

const HandleGroupAccessOwnerActions: Reducer<IHierarchyReportingAccessState, GroupAccessOwnerViewActions> = (state, action) => {
	const groupAccessOwnersFetchedFor = new Set(
		state.hierarchyReportingAccessConfigs
			.map((config) => config.hierarchyReportingAccessConfigId)
			.filter((configId) => configId && GetViewIdentifier(configId) === action.forView),
	);

	// If group access owners were not fetched on behalf of any reporting access config views
	if (groupAccessOwnersFetchedFor.size === 0) {
		return state;
	}

	switch (action.type) {
		case GroupAccessOwnerViewActionTypes.COLUMN_CONSTRUCTION_STARTED:
		case GroupAccessOwnerViewActionTypes.REQUEST_PEOPLE:
			return {
				...state,
				fetchingGroupAccessOwners: true,
			};
		case GroupAccessOwnerViewActionTypes.COLUMN_CONSTRUCTION_FINISHED:
			return {
				...state,
				fetchingGroupAccessOwners: false,
			};
		case GroupAccessOwnerViewActionTypes.PEOPLE_FETCHED: {
			const total = action.people.pageRange.total;

			// update any profile where the fetched amount is different from what is stored in survey builder.
			const outOfSyncHierarchyReportingAccessConfigs: number[] = [];
			const hierarchyReportingAccessConfigs = state.hierarchyReportingAccessConfigs.map((c) => {
				const configId = c.hierarchyReportingAccessConfigId;

				if (configId && groupAccessOwnersFetchedFor.has(configId) && total && c.userCount !== total) {
					outOfSyncHierarchyReportingAccessConfigs.push(configId);
					return { ...c, userCount: total };
				}

				return c;
			});

			return {
				...state,
				fetchingGroupAccessOwners: false,
				hierarchyReportingAccessConfigs,
				outOfSyncHierarchyReportingAccessConfigId: outOfSyncHierarchyReportingAccessConfigs,
			};
		}
		case GroupAccessOwnerViewActionTypes.COLUMN_CONSTRUCTION_FAILED:
		case GroupAccessOwnerViewActionTypes.PEOPLE_COULDNOT_BEFETCHED:
			return {
				...state,
				fetchingGroupAccessOwners: 'N/A',
			};
		default:
			return state;
	}
};
